/**
 * Integrytas Group - Idempotency Key Engine (IKE)
 * Page: IKE.KEYS
 *
 * Observability for the gateway idempotency barrier: live counts by status and
 * the most recent keys (which request claimed them, whether a response is cached
 * for replay, and when each expires). Purge clears keys past their TTL.
 */

import { Fragment, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { RelatedChildrenPanel, RelatedExpandButton, useExpandedRows, type RelatedChildGroup } from '../../components/common/RelatedChildrenPanel';
import { PersonalizationProvider, PersonalizationToggle, PersonalizedGrid } from '../../components/personalization';
import type { GridColumnDef } from '../../components/personalization';
import { apiDb } from '../../lib/api_client';
import { EmptyState } from '../../components/common/EmptyState';
import { ErrorBanner } from '../../components/common/ErrorBanner';
import { StatusBadge, type StatusVariant } from '../../components/common/StatusBadge';
import { hasAccess, type AuthSession } from '../../types/auth';

interface KeyRow {
  idempotency_key: string; user_id: number; workspace_code: string;
  request_kind: string | null; request_endpoint: string | null;
  status: 'LOCKED' | 'COMPLETED' | 'FAILED'; response_status: number | null;
  locked_at: string; completed_at: string | null; expires_at: string;
}
interface Stats { locked: number; completed: number; failed: number; expired: number; total: number }

// Key status (CHECK enum) → localized label + badge variant (§17.B11).
const STATUS_META: Record<string, { labelKey: string; label: string; variant: StatusVariant }> = {
  LOCKED:    { labelKey: 'ike.status.LOCKED',    label: 'Locked',    variant: 'warning' },
  COMPLETED: { labelKey: 'ike.status.COMPLETED', label: 'Completed', variant: 'success' },
  FAILED:    { labelKey: 'ike.status.FAILED',    label: 'Failed',    variant: 'danger'  },
};

const KEY_GRID_COLS: GridColumnDef[] = [
  { key: 'status',     header: 'Status',    defaultWidth: 110 },
  { key: 'key',        header: 'Key',       defaultWidth: 200 },
  { key: 'user',       header: 'User',      defaultWidth: 150 },
  { key: 'workspace',  header: 'Workspace', defaultWidth: 130 },
  { key: 'request',    header: 'Request',   defaultWidth: 240 },
  { key: 'claimed',    header: 'Claimed',   defaultWidth: 170 },
  { key: 'expires',    header: 'Expires',   defaultWidth: 170 },
];

export interface IkeKeysPageProps { session: AuthSession }

export function IkeKeysPage({ session }: IkeKeysPageProps) {
  return (
    <PersonalizationProvider pageCode="IKE.KEYS" session={session}>
      <IkeKeysPageInner session={session} />
    </PersonalizationProvider>
  );
}


const PAGE_CODE = 'IKE.KEYS';
function IkeKeysPageInner({ session }: IkeKeysPageProps) {
  // §6/§17: the write below is page-scoped, so the affordance follows
  // csm_role_page_access. The server refuses it regardless; this stops the
  // page offering an action that can only error.
  const canDelete = hasAccess(session, PAGE_CODE, 'delete');
  const { t } = useTranslation();
  const [rows, setRows] = useState<KeyRow[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  const [userNames, setUserNames] = useState<Record<number, string>>({});
  // §17.B13 related-children disclosure. An idempotency-key row is a log entry with
  // no child collection, so the control renders inert until relations are wired.
  const detail = useExpandedRows<string>();
  const buildRelatedGroups = (_id: string): RelatedChildGroup[] => []; // TODO: wire relations
  const hasRelated = (id: string) => buildRelatedGroups(id).some(g => g.items.length > 0);

  const reload = async () => {
    setBusy(true); setError(null);
    try {
      const [k, s] = await Promise.all([
        apiDb.from('ike_key').select('idempotency_key, user_id, workspace_code, request_kind, request_endpoint, status, response_status, locked_at, completed_at, expires_at').order('locked_at', { ascending: false }).limit(200),
        apiDb.rpc<Stats>('ike_stats'),
      ]);
      if (k.error) throw k.error;
      const keyRows = (k.data ?? []) as KeyRow[];
      setRows(keyRows);
      setStats((s.data ?? null) as Stats | null);
      const ids = Array.from(new Set(keyRows.map(r => r.user_id).filter((x): x is number => !!x)));
      if (ids.length) {
        const { data: us } = await apiDb.from('csm_user_master').select('user_id, display_name, email').in('user_id', ids);
        const m: Record<number, string> = {};
        for (const u of (us ?? []) as { user_id: number; display_name: string | null; email: string }[]) m[u.user_id] = u.display_name || u.email || '—';
        setUserNames(m);
      } else { setUserNames({}); }
    } catch (err) { setError((err as Error).message); } finally { setBusy(false); }
  };
  useEffect(() => { void reload(); }, []);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(r =>
      r.idempotency_key.toLowerCase().includes(q) ||
      (r.workspace_code ?? '').toLowerCase().includes(q) ||
      r.status.toLowerCase().includes(q) ||
      (r.request_endpoint ?? '').toLowerCase().includes(q));
  }, [rows, filter]);

  const purge = async () => {
    setBusy(true); setError(null);
    try { const { error: e } = await apiDb.rpc('ike_purge'); if (e) throw e; await reload(); }
    catch (err) { setError((err as Error).message); } finally { setBusy(false); }
  };

  const now = Date.now();

  return (
    <div className="flex h-full flex-col">
      <div className="flex h-10 shrink-0 items-center gap-2 border-b border-csm-border bg-white px-3">
        <span className="csm-page-title">{t('ike.title', 'Idempotency Keys')}</span>
        <input
          type="text"
          value={filter}
          onChange={e => setFilter(e.target.value)}
          placeholder={t('ike.filter_placeholder', 'Filter keys…')}
          className="h-7 w-60 rounded border border-csm-border px-2 text-csm-md focus:border-csm-watermark focus:outline-none"
        />
        <button onClick={() => void reload()} className="h-7 rounded border border-csm-border bg-white px-2 text-csm-sm hover:bg-csm-surface-alt">{t('ike.refresh', 'Refresh')}</button>
        {canDelete && (<button onClick={() => void purge()} className="h-7 rounded border border-csm-border bg-white px-2 text-csm-sm hover:bg-csm-surface-alt">{t('ike.purge', 'Purge expired')}</button>)}
        {busy && <span className="text-csm-xs text-csm-watermark">{t('ike.working', 'Working…')}</span>}
        <div className="ml-auto"><PersonalizationToggle /></div>
      </div>
      {error && (
        <ErrorBanner
          message={t('ike.error_banner', "Couldn't complete the last idempotency-keys operation.")}
          detail={error}
          retry_label={t('common.retry', 'Retry')}
          on_retry={() => void reload()}
          details_label={t('common.details', 'Details')}
        />
      )}

      <div className="flex-1 overflow-auto p-3">
        <div className="mb-3 flex flex-wrap gap-2">
          <Stat label={t('ike.stat.active_locks', 'Active locks')} value={stats?.locked} tone="amber" />
          <Stat label={t('ike.stat.cached', 'Cached (replayable)')} value={stats?.completed} tone="green" />
          <Stat label={t('ike.stat.failed', 'Failed (released)')} value={stats?.failed} tone="red" />
          <Stat label={t('ike.stat.expired', 'Expired')} value={stats?.expired} />
          <Stat label={t('ike.stat.total', 'Total')} value={stats?.total} />
        </div>

        <div className="overflow-x-auto">
          <PersonalizedGrid componentKey="grid" columns={KEY_GRID_COLS}>
            {(visibleCols, widths) => (
              <table className="w-max min-w-full border-collapse text-csm-sm">
                <thead><tr className="border-b border-csm-border text-left text-csm-2xs uppercase tracking-wide text-csm-watermark">
                  <th className="w-7 px-2 py-1" title={t('ike.related.header', 'Related records')} />
                  {visibleCols.map(col => (
                    <th key={col.key} className="px-2 py-1"
                        style={widths[col.key] ? { width: widths[col.key] } : undefined}>{t('ike.col.' + col.key, col.header)}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {filtered.map(r => {
                    const expired = new Date(r.expires_at).getTime() <= now;
                    const rowKey = `${r.idempotency_key}:${r.user_id}:${r.workspace_code}`;
                    return (
                      <Fragment key={rowKey}>
                      <tr className={`border-b border-csm-border-soft ${expired ? 'opacity-50' : ''}`}>
                        <td className="px-1 py-1 text-center align-middle" onClick={e => e.stopPropagation()}>
                          <RelatedExpandButton open={detail.isOpen(rowKey)} hasChildren={hasRelated(rowKey)} onToggle={() => detail.toggle(rowKey)} expandLabel={t('ike.related.expand', 'Show related records')} collapseLabel={t('ike.related.collapse', 'Hide related')} tooltip={t('ike.related.tooltip', 'Show related records')} />
                        </td>
                        {visibleCols.map(col => (
                          <td key={col.key} className="px-2 py-1"
                              style={widths[col.key] ? { width: widths[col.key] } : undefined}>
                            {col.key === 'status'    && (() => { const meta = STATUS_META[r.status]; return <StatusBadge variant={meta?.variant ?? 'neutral'} label={meta ? t(meta.labelKey, meta.label) : r.status} />; })()}
                            {col.key === 'key'       && <span className="font-mono text-csm-2xs" title={r.idempotency_key}>{r.idempotency_key.length > 20 ? `${r.idempotency_key.slice(0, 20)}…` : r.idempotency_key}</span>}
                            {col.key === 'user'      && <span className="text-csm-xs">{r.user_id ? (userNames[r.user_id] ?? '—') : '—'}</span>}
                            {col.key === 'workspace' && <span className="text-csm-xs">{r.workspace_code || '—'}</span>/* fk-display-ok: ASF workspace technical key, no named entity */}
                            {col.key === 'request'   && <span className="font-mono text-csm-2xs text-csm-ink-soft">{r.request_kind} {r.request_endpoint ?? ''}{r.response_status ? ` → ${r.response_status}` : ''}</span>}
                            {col.key === 'claimed'   && <span className="text-csm-2xs text-csm-watermark">{new Date(r.locked_at).toLocaleString()}</span>}
                            {col.key === 'expires'   && <span className="text-csm-2xs text-csm-watermark">{new Date(r.expires_at).toLocaleString()}</span>}
                          </td>
                        ))}
                      </tr>
                      {detail.isOpen(rowKey) && (
                        <tr>
                          <td colSpan={visibleCols.length + 1} className="border-b border-csm-border bg-csm-surface-alt/40 px-3 py-3" onClick={e => e.stopPropagation()}>
                            <RelatedChildrenPanel groups={buildRelatedGroups(rowKey)} session={session} />
                          </td>
                        </tr>
                      )}
                      </Fragment>
                    );
                  })}
                  {filtered.length === 0 && <tr><td colSpan={visibleCols.length + 1} className="px-2 py-6 text-center">
                    <EmptyState variant={filter.trim() ? 'no-match' : 'no-data'} title={t('ike.empty', 'No idempotency keys recorded yet.')} />
                  </td></tr>}
                </tbody>
              </table>
            )}
          </PersonalizedGrid>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, tone }: { label: string; value: number | undefined; tone?: 'amber' | 'green' | 'red' }): ReactNode {
  const toneCls = tone === 'amber' ? 'text-amber-700' : tone === 'green' ? 'text-green-700' : tone === 'red' ? 'text-red-700' : 'text-csm-ink';
  return (
    <div className="rounded border border-csm-border-soft bg-white px-3 py-1.5">
      <div className={`text-csm-base font-semibold ${toneCls}`}>{value ?? '—'}</div>
      <div className="text-csm-2xs uppercase tracking-wide text-csm-watermark">{label}</div>
    </div>
  );
}
